import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BrandLogo, BrandMark } from '@/components/Brand';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useLive } from '@/hooks/LiveProvider';
import { useT } from '@/i18n';
import { cn } from '@/lib/cn';
import { formatBitrate } from '@/lib/format';
import { paths } from '@/router/paths';
import { adminGroup, isNavGroup, navEntries, type NavEntry } from './nav-config';

const linkBase =
  'flex items-center gap-3 rounded-[11px] px-2.5 py-2.5 text-[13px] font-semibold text-t1 transition-colors hover:bg-bg-3 hover:text-t0';
const linkActive = 'bg-acc-soft text-acc!';

function leafMatches(to: string, path: string): boolean {
  return path === to || path.startsWith(to.endsWith('/') ? to : `${to}/`);
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0 text-t2 transition-transform duration-200', open && 'rotate-180')}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function SidebarContent({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const { isAdmin } = useAuth();
  const { snapshots } = useLive();

  const adminOwnsRoute = adminGroup.items.some((item) => leafMatches(item.to, location.pathname));
  const [adminOpen, setAdminOpen] = useState(adminOwnsRoute);

  useEffect(() => {
    if (adminOwnsRoute) setAdminOpen(true);
  }, [adminOwnsRoute]);

  // Gesamtdurchsatz ueber alle sichtbaren Instanzen fuer die Statuskarte
  const live = Object.values(snapshots);
  const throughput = live.reduce(
    (sum, snapshot) => sum + snapshot.rxBitsPerSecond + snapshot.txBitsPerSecond,
    0,
  );
  const onlinePeers = live.reduce((sum, snapshot) => sum + snapshot.onlinePeers, 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center px-5 py-[18px]" style={{ minHeight: 74 }}>
        {collapsed ? <BrandMark size={34} /> : <BrandLogo size={34} showTagline />}
      </div>

      <nav className="flex-1 overflow-x-hidden overflow-y-auto px-3.5 pb-3.5">
        {!collapsed && (
          <p className="px-2.5 pt-1 pb-1.5 text-[10.5px] font-bold tracking-wider text-t2 uppercase">
            {t('nav.section')}
          </p>
        )}

        {navEntries.map((entry: NavEntry) => {
          if (isNavGroup(entry)) return null;
          const Icon = entry.icon;
          return (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.to === paths.dashboard}
              onClick={onNavigate}
              title={collapsed ? t(entry.labelKey) : undefined}
              className={({ isActive }) =>
                cn(linkBase, collapsed && 'justify-center', isActive && linkActive)
              }
            >
              <Icon size={18} strokeWidth={1.9} className="shrink-0" />
              {!collapsed && <span className="flex-1">{t(entry.labelKey)}</span>}
            </NavLink>
          );
        })}

        {isAdmin && (
          <>
            {!collapsed && (
              <p className="px-2.5 pt-4 pb-1.5 text-[10.5px] font-bold tracking-wider text-t2 uppercase">
                {t('nav.operations')}
              </p>
            )}
            <div>
              <button
                type="button"
                onClick={() => setAdminOpen((open) => !open)}
                title={collapsed ? t(adminGroup.labelKey) : undefined}
                className={cn(linkBase, 'w-full justify-between', collapsed && 'justify-center')}
              >
                <span className="flex items-center gap-3">
                  <adminGroup.icon size={18} strokeWidth={1.9} className="shrink-0" />
                  {!collapsed && t(adminGroup.labelKey)}
                </span>
                {!collapsed && <Chevron open={adminOpen} />}
              </button>

              {!collapsed && adminOpen && (
                <div className="mt-1 ml-[21px] flex flex-col gap-0.5 border-l border-line pl-4">
                  {adminGroup.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-[12.5px] font-medium text-t1 hover:bg-bg-3 hover:text-t0',
                          isActive && linkActive,
                        )
                      }
                    >
                      <span
                        className="h-[5px] w-[5px] shrink-0 rounded-full"
                        style={{ background: item.dot ?? 'var(--t2)' }}
                      />
                      <span className="flex-1 truncate">{t(item.labelKey)}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </nav>

      {!collapsed && (
        <div className="relative m-3.5 mt-0 overflow-hidden rounded-[var(--radius-vela-lg)] border border-line bg-bg-3 p-4">
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(120% 90% at 100% 0%, var(--acc-soft), transparent 60%)' }}
          />
          <div className="relative mb-2 flex items-center gap-2">
            <span className="animate-live h-1.5 w-1.5 rounded-full bg-acc" />
            <p className="text-[13.5px] font-bold text-t0">
              {onlinePeers === 1
                ? t('live.peerOnline', { count: onlinePeers })
                : t('live.peersOnline', { count: onlinePeers })}
            </p>
          </div>
          <p className="relative mb-3 font-mono text-xs text-t1">
            {formatBitrate(throughput)} {t('common.total')}
          </p>
          <Button
            size="sm"
            fullWidth
            className="relative"
            onClick={() => {
              onNavigate?.();
              navigate(paths.plans);
            }}
          >
            <Plus size={14} />
            {t('dashboard.orderPlan')}
          </Button>
        </div>
      )}
    </div>
  );
}
